import { useEffect, useRef, useState } from 'react';

const WA_LINK = 'https://wa.me/5541992570505?text=Ol%C3%A1%2C%20gostaria%20de%20saber%20mais%20sobre%20os%20servi%C3%A7os%20da%20KPR%20Advogados.';

const empresarial = [
  {
    icon: 'ri-building-2-line',
    title: 'Direito Societário',
    desc: 'Constituição, reestruturação e dissolução de sociedades. Acordos de sócios e governança corporativa para empresas de todos os portes.',
  },
  {
    icon: 'ri-file-text-line',
    title: 'Contratos Empresariais e Bancários',
    desc: 'Elaboração, revisão e negociação de contratos complexos, incluindo operações bancárias, financiamentos e instrumentos de crédito.',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Recuperação Judicial',
    desc: 'Estratégias jurídicas para reestruturação de passivos, negociação com credores e proteção de ativos empresariais em momentos críticos.',
  },
  {
    icon: 'ri-shield-line',
    title: 'Conformidade (Compliance)',
    desc: 'Implementação de programas de integridade, políticas anticorrupção e adequação às normas regulatórias setoriais.',
  },
  {
    icon: 'ri-drop-line',
    title: 'Setor de Óleo & Gás',
    desc: 'Assessoria jurídica especializada em contratos, licenciamento e operações no setor de óleo e gás, com foco em segurança jurídica.',
  },
  {
    icon: 'ri-plant-line',
    title: 'Agronegócio',
    desc: 'Soluções jurídicas para o setor agrícola: contratos rurais, financiamentos, regularização fundiária e planejamento patrimonial rural.',
  },
  {
    icon: 'ri-lightbulb-line',
    title: 'Propriedade Intelectual',
    desc: 'Registro e proteção de marcas, patentes e ativos intangíveis. Estratégias para valorização e monetização do conhecimento.',
  },
  {
    icon: 'ri-funds-line',
    title: 'Participações e Estrutura Patrimonial',
    desc: 'Constituição de holdings, fundos e estruturas de participação para otimização fiscal, sucessão e proteção patrimonial.',
  },
];

const leiloes = [
  {
    icon: 'ri-search-eye-line',
    title: 'Análise Jurídica de Imóveis',
    desc: 'Due diligence completa de imóveis em leilão: certidões, ônus, débitos e riscos jurídicos antes da arrematação.',
  },
  {
    icon: 'ri-auction-line',
    title: 'Assessoria Completa em Leilões',
    desc: 'Acompanhamento integral do processo de leilão judicial e extrajudicial, da análise inicial à arrematação.',
  },
  {
    icon: 'ri-key-2-line',
    title: 'Regularização Pós-arrematação',
    desc: 'Imissão na posse, cancelamento de ônus e registros, habite-se e todos os procedimentos para transferência segura do imóvel.',
  },
  {
    icon: 'ri-trophy-line',
    title: 'Estratégias para Investidores',
    desc: 'Consultoria especializada para investidores: identificação de oportunidades, análise de rentabilidade e gestão de riscos em leilões.',
  },
];

interface ServiceCardProps {
  icon: string;
  title: string;
  desc: string;
  delay: number;
  visible: boolean;
}

function ServiceCard({ icon, title, desc, delay, visible }: ServiceCardProps) {
  return (
    <div
      className="group relative border border-white/8 hover:border-kpr-gold/40 p-7 transition-all duration-400 cursor-default overflow-hidden"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms, border-color 0.3s`,
        background: 'rgba(255,255,255,0.04)',
      }}
    >
      {/* Hover gold glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-kpr-gold/0 via-transparent to-kpr-gold/0 group-hover:from-kpr-gold/5 group-hover:to-kpr-gold/3 transition-all duration-500" />

      {/* Icon + Title inline */}
      <div className="relative flex items-center gap-3 mb-4">
        <div className="w-8 h-8 flex items-center justify-center border border-kpr-gold/25 group-hover:border-kpr-gold/60 shrink-0 transition-colors duration-300">
          <i className={`${icon} text-kpr-gold text-sm w-4 h-4 flex items-center justify-center`} />
        </div>
        <h4 className="font-serif text-white/90 font-light text-base leading-snug">
          <a className="hover:text-kpr-gold transition-colors duration-200">{title}</a>
        </h4>
      </div>

      <p className="relative text-white/40 text-sm leading-relaxed mb-5 font-light">{desc}</p>
      <a
        href={WA_LINK}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="relative inline-flex items-center gap-1.5 text-kpr-gold/70 hover:text-kpr-gold text-xs tracking-wider uppercase font-medium transition-colors duration-200 cursor-pointer group/btn"
      >
        Saiba mais
        <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center group-hover/btn:translate-x-1 transition-transform duration-200" />
      </a>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 h-px w-0 bg-kpr-gold group-hover:w-full transition-all duration-400" />
    </div>
  );
}

export default function Services() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="servicos" className="bg-kpr-dark py-28 lg:py-36 relative overflow-hidden" ref={ref}>
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: 'repeating-linear-gradient(45deg, #C9A96E 0, #C9A96E 1px, transparent 0, transparent 50%)', backgroundSize: '20px 20px' }}
      />

      <div className="relative max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="mb-20">
          <div className={`flex items-center gap-4 mb-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="w-10 h-px bg-kpr-gold" />
            <span className="text-kpr-gold text-xs font-medium tracking-[0.25em] uppercase">Áreas de Atuação</span>
          </div>
          <h2 className={`font-serif text-4xl md:text-5xl text-white font-light mb-5 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Serviços Jurídicos Especializados
          </h2>
          <p className={`text-white/45 max-w-xl text-[15px] leading-loose font-light transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Atuamos com precisão técnica e visão estratégica nas principais áreas do direito empresarial
            e de investimentos, oferecendo soluções sob medida para cada cliente.
          </p>
        </div>

        {/* Block 1: Assessoria Empresarial */}
        <div className="mb-16">
          <div className={`flex items-center gap-5 mb-8 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <h3 className="font-serif text-xl text-white/80 font-light tracking-wide">Assessoria Empresarial</h3>
            <div className="flex-1 h-px bg-white/8" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
            {empresarial.map((s, i) => (
              <ServiceCard key={s.title} {...s} delay={i * 60} visible={visible} />
            ))}
          </div>
        </div>

        {/* Block 2: Assessoria em Leilões */}
        <div>
          <div className={`flex items-center gap-5 mb-8 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <h3 className="font-serif text-xl text-white/80 font-light tracking-wide">Assessoria em Leilões</h3>
            <div className="flex-1 h-px bg-white/8" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
            {leiloes.map((s, i) => (
              <ServiceCard key={s.title} {...s} delay={600 + i * 60} visible={visible} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
